import { useGroupUserRoles, useOrganizationInfo, useUserId } from '@features/user';
import { hasAnyRole, hasRole, ROLES } from '@utils/roleUtils';

const ORG_SCHEDULE_ROLES = [
  ROLES.PRACTICE_ADMINISTRATOR,
  ROLES.BILLING_ADMINISTRATOR,
  ROLES.OPERATIONS_SUPPORT,
];

/**
 * Pure access-control hook for org-level schedule features.
 *
 * Access is granted by the user's org roles AND whether the organization
 * is a group (more than one member).
 *
 * Does NOT fetch any data — data concerns (e.g. org members)
 * belong in the filter hook or filter bar component.
 */
export function useOrgScheduleAccess() {
  const groupUserRoles = useGroupUserRoles();
  const currentUserId = useUserId();
  const orgInfo = useOrganizationInfo();

  const isGroupOrg = orgInfo?.group ?? false;
  const hasOrgScheduleRole = hasAnyRole(groupUserRoles, ORG_SCHEDULE_ROLES);
  const hasOrgScheduleAccess = hasOrgScheduleRole && isGroupOrg;
  // Whether the current user can themselves be an appointment provider —
  // i.e. appears in the org's clinician list (GDN-2225).
  const isCurrentUserClinician = hasRole(groupUserRoles, ROLES.CLINICIAN);

  return {
    hasOrgScheduleAccess,
    hasOrgScheduleRole,
    isGroupOrg,
    isCurrentUserClinician,
    currentUserId,
  };
}
